<template>
    <div>
        <DataTable
            :value="users"
            :lazy="true"
            paginator
            :rows="10"
            @page="onPage"
            @sort="onSort"
            sortMode="multiple"
            selectionMode="single"
            :loading="loading"
            dataKey="id"
            :totalRecords="totalUserRecords"
            responsiveLayout="scroll"
            :globalFilterFields="['code', 'description']"
            :rowsPerPageOptions="[5, 10, 20, 50]"
            paginatorTemplate="CurrentPageReport JumpToPageInput FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries :: Page No:"
        >
            <template #empty> No Users available. </template>
            <Column field="id" header="USER ID"></Column>
            <Column field="name" header="NAME" >
                <template #body="slotProps">
                    <div class="capitalize" @click="selectUser(slotProps.data.id)">
                        {{ slotProps.data.name }}
                    </div>
                </template>
            </Column>
            <Column field="surname" header="SURNAME">
                <template #body="slotProps">
                    <div class="capitalize" @click="selectUser(slotProps.data.id)">
                        {{ slotProps.data.surname }}
                    </div>
                </template>
            </Column>
            <Column field="email" header="EMAIL">
                <template #body="slotProps">
                    <div @click="selectUser(slotProps.data.id)">
                        {{ slotProps.data.email }}
                    </div>
                </template>
            </Column>
            <Column field="locked" header="UNLOCK" style="min-width: 2rem">
                <template #body="slotProps">
                    <Button v-if="slotProps.data.locked === 1"
                            icon="pi pi-key"  severity="help" raised rounded class=" p-button-outlined mr-2"
                            @click="unlockUserDialog(slotProps.data)" :disabled="unlockingInProgress"

                    />
                </template>
            </Column>
            <Column header="ACTIONS" style="width: 300px">
                <template #body="slotProps">
                    <Button
                        icon="pi pi-bolt"
                        class="p-button-rounded p-button-help"
                        @click="selectUser(slotProps.data.id)"
                        label="Assessments"
                        v-tooltip="'Start an assessment'"
                    />
                    <Button
                        v-if="checkUserPermission(UserPermissions.ALLOW_ARCHIVE_USER)"
                        icon="pi pi-folder-open"
                        class="p-button-rounded p-button-warning ml-2"
                        @click="archiveUser(slotProps.data.id)"
                        v-tooltip="slotProps.data.is_archived ? 'Unarchive' : 'Archive'"
                    />
                    <Button
                        icon="pi pi-pencil"
                        class="p-button-rounded p-button-success ml-2"
                        @click="editUser(slotProps.data.id)"
                        v-tooltip="'Edit'"
                    />
                    <Button
                        icon="pi pi-trash"
                        class="p-button-rounded p-button-danger ml-2"
                        @click="deleteUser(slotProps.data.id)"
                        v-tooltip="'Delete'"
                    />
                </template>
            </Column>
        </DataTable>
        <Dialog close-on-escape v-model:visible="userLockedDialogVisible" :style="{ width: '450px' }" header="User locked" :modal="true" class="p-fluid dialog-header">
            <div>
                This account has been locked due to a number of failed login attempts at
                <b>{{formatDate(currentUserDetails.locked_email_sent_at)}}</b>.
            </div>
            <br />

            <h6>
                Do you want to unlock their account?
            </h6>
            <br />
            <p>
                It's advised that the user resets their password once the account has been unlocked.
            </p>

            <template #footer>
                <Button label="Cancel" icon="pi pi-times" class="p-button-danger" @click="userLockedDialogVisible = false" />
                <Button icon="pi pi-key" severity="help" class="p-button-primary mr-2" label="Unlock"
                        @click="unlockUser" :disabled="unlockingInProgress" :loading="unlockingInProgress"
                />
            </template>
        </Dialog>
    </div>

    <add-user v-if="userDialogDisplay" @hide-dialog="userDialogDisplay = false" :editId="userToEdit" />
</template>

<script setup>
// computed,
import { ref, onMounted, defineProps, watch } from 'vue';
import { useRouter } from 'vue-router';
import useUserComposable from '@/composables/UserComposable';
import AddUser from '@/components/user/AddUser.vue';
import GlobalRequest from '@/api/global-request';
import { showToast } from '@/helpers/toast-handler';
import { DateTime } from 'luxon';
import { useStore } from 'vuex';
import { UserPermissions } from '@/enums';

const store = useStore();
const companyId = store.getters['auth/userCompanyId']?.id   ;

const router = useRouter();

const { loading, users, totalUserRecords, getAllUsers, checkUserPermission } = useUserComposable();
const lazyParams = ref({});
const userDialogDisplay = ref(false)

const userToEdit = ref(null)


const props = defineProps({
    criteriaFilters: {
        type: Object,
        default: null,
    },
    reloadTable: {
        type: Boolean,
    },
});

// when a criteria is loaded, it triggers the reloadTable props which will trigger the reload of the table
watch(
    () => props.reloadTable,
    () => {
        loadLazyData();
    }
);

onMounted(async () => {
    lazyParams.value = {
        first: 0,
        rows: 10,
        page: null,
        sort: null,
    };
    await loadLazyData();
});

const loadLazyData = async () => {
    await getAllUsers({
        page_size: lazyParams.value.rows,
        page: lazyParams.value.page,
        sort: lazyParams.value.sort,
        ...props.criteriaFilters,
    });
};
const unlockingInProgress = ref(false)


const userLockedDialogVisible = ref(false)
const currentUserDetails = ref({})

function unlockUserDialog(data) {
    userLockedDialogVisible.value = true
    currentUserDetails.value = data
}

function formatDate(dateString) {
    console.log('dateString', dateString)
    return dateString ? DateTime.fromISO(dateString).setLocale('en-GB').toFormat('dd-MM-yyyy at HH:mm') : '';
}

function unlockUser() {
    const data = currentUserDetails.value

    if (typeof data !== 'undefined' && Object.hasOwn(data, 'id')) {
        unlockingInProgress.value = true;
        const userId = data.id;


        const apiUrl = '/v2/admin/users/unlock';

        GlobalRequest.postRequest(apiUrl, {
            company_id: companyId,
            users: [userId]
        }).then(async (response) => {

            console.log('Full API Response:', response); // Debugging

            if (response?.statusCode === 200) { // Safe access with optional chaining
                await loadLazyData();
                showToast({ severity: 'success', summary: 'User unlocked' }, 'User unlocked').then(() => {
                    unlockingInProgress.value = false;
                    userLockedDialogVisible.value = false;
                });
            } else {
                console.log('API Response:', response);
                showToast({ severity: 'error', summary: 'Error' }, response?.data?.message || 'Failed to unlock user');
                unlockingInProgress.value = false;
                userLockedDialogVisible.value = false;
            }
        }).catch((error) => {
            console.log('Request Failed:', error);
            showToast({ severity: 'error', summary: 'Error' }, error?.response?.data?.message || 'An unexpected error occurred');
            unlockingInProgress.value = false;
            userLockedDialogVisible.value = false;
        });
    }
}

const onPage = async (event) => {
    lazyParams.value = event;
    await loadLazyData();
};

const onSort = (event) => {
    const sortings = event.multiSortMeta;
    lazyParams.value.sort = sortings
        .map((item) => {
            return item.field + ' ' + (item.order === 1 ? 'asc' : 'desc');
        })
        .join(',');
    loadLazyData();
};

function editUser(id) {
    userToEdit.value = id;
    userDialogDisplay.value = true
}

function deleteUser(id) {
    GlobalRequest.deleteRequest(`/v2/admin/users/${id}`)
        .then(() => {
            loadLazyData();
        })
}


function archiveUser(id) {
    GlobalRequest.patchRequest(`/v2/admin/users/archive/${id}`)
        .then(() => {
            loadLazyData();
        })
}


function selectUser(id) {
    router.push({ name: 'UserSpecificAnswersTable', params: { userId: id } });
}
</script>
